import { FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectPlacementOrderType } from 'store/redux-store/placement-details/selectors';
import { PlacementOrderType } from 'app/models/Placement/PlacementDetails';
import {
  selectEditableRequirementTypes,
  selectPicklists,
  selectBookingInfo,
} from '../../../../../../../../../store/redux-store/placement-summary/selectors';
import { getRequirementCheck } from '../../../helper';
import { TransportationTypes } from './TransportationWrapper';
import { selectUser } from 'oidc/user.selectors';
import { convertToPST } from 'app/helpers/dateHelper';
import { Concatenate } from 'utils/string/string';
import { useArrivedFieldsetMeta } from '../useArrivedFieldsetMeta';
import { CustomTooltip } from '@AMIEWEB/Common';

const useStyles = makeStyles()({
  radioRoot: {
    marginRight: '24px',
  },
  radio: {
    padding: 0,
    paddingRight: '6px',
  },
});

interface TransportationsHeaderProps {
  disabled?: boolean;
}

export const TransportationsHeader = ({ disabled = false, ...props }: TransportationsHeaderProps) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const { transportationOpts } = useSelector(selectPicklists);
  const { transportation } = useSelector(selectEditableRequirementTypes);
  const { getValues, setValue } = useFormContext();
  const placementOrderType = useSelector(selectPlacementOrderType);
  const { confirmation: { arrived } } = useSelector(selectBookingInfo);
  const { userInfo } = useSelector(selectUser);
  const isStrike = placementOrderType === PlacementOrderType.StrikeOrderInHsg;
  const isNotArrived = !arrived?.data?.checked;

  const fieldsetMeta = useArrivedFieldsetMeta();
  disabled = disabled || fieldsetMeta.disabled;

  return (
    <Grid container spacing={4} wrap="nowrap" alignItems="center">
      {transportation !== null && (
        <Grid style={{ width: '32px', minWidth: '32px' }} item>
          {getRequirementCheck(transportation?.requirement)}
        </Grid>
      )}
      <Grid item>
        <Typography>{t('placement.profile.summary.offer.transportation.title')}</Typography>
      </Grid>
      <Grid item>
        <Controller
          name="transportationChosen"
          render={({ field: { onChange, value, ...rest } }) => (
            <CustomTooltip  
              disabled={!fieldsetMeta.disabled} 
              tooltipContent={fieldsetMeta.title} 
              arrow={false} isStandardContent
            >
              <RadioGroup
                value={value?.transportationId}
                row
                onChange={(_, newValueId) => {
                  const newValue = transportationOpts
                    .find(item => item?.transportationId === Number.parseInt(newValueId));

                  const newValueIsUnknown = newValue?.transportationId === TransportationTypes.UNKNOWN;
                  const previousWasDriveOrFly =
                    value?.transportationId === TransportationTypes.DRIVE || 
                    value?.transportationId === TransportationTypes.FLY;

                  if (isStrike && isNotArrived && previousWasDriveOrFly && newValueIsUnknown) {
                    const now = convertToPST(new Date(), 'yyyy-MM-DDTHH:mm:ss');
                    const userName = Concatenate([userInfo?.firstName || '', userInfo?.lastName || ''], ' ');
                    const userId = userInfo?.employeeId || null;

                    const { datesConfirmed, travelConfirmed } = getValues();

                    if (datesConfirmed?.checked) {
                      setValue('datesConfirmed', {
                        checked: false,
                        lastUpdatedBy: userName,
                        lastUpdatedById: userId,
                        lastUpdatedAt: now,
                      }, { shouldDirty: true });
                    }
                    if (travelConfirmed?.checked) {
                      setValue('travelConfirmed', {
                        checked: false,
                        lastUpdatedBy: userName,
                        lastUpdatedById: userId,
                        lastUpdatedAt: now,
                      }, { shouldDirty: true });
                    }
                  }

                  onChange(newValue);
                }}
              >
                {transportationOpts.map(item => (
                    <FormControlLabel
                      key={item?.transportationId}
                      disabled={disabled}
                      classes={{ root: classes.radioRoot }}
                      className={cx('input', { disabled: disabled })}
                      value={item?.transportationId}
                      control={<Radio size="medium" className={classes.radio} />}
                      label={item.transportation}
                    />
                ))}
              </RadioGroup>
            </CustomTooltip>
          )}
        />
      </Grid>
    </Grid>
  );
};
